import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { errorFor } from './form-controls';
import { CustomerSearchSelect } from './customer-search-select';
import { ContactSelect } from './contact-select';
import { SectionShell, FieldGrid } from './section-shell';
import type { ContractFormValues } from './contract-form';

/**
 * Customer + contact section. Selecting a customer is required (async
 * search-select) and resets the dependent contact + products; the contact
 * dropdown then loads that customer's signatories. The picked contact's roles
 * are bubbled to the page so the agreement section can gate the `supplier` role.
 */
export function CustomerSection({ onRolesChange }: { onRolesChange: (roles: string[]) => void }) {
  const { control, formState, setValue } = useFormContext<ContractFormValues>();
  const customerId = useWatch({ control, name: 'customer_id' });
  const customerError = errorFor(formState.errors, 'customer_id');

  return (
    <SectionShell title="Customer" description="Who is this contract for?">
      <FieldGrid>
        <Controller
          control={control}
          name="customer_id"
          render={({ field }) => (
            <CustomerSearchSelect
              label="Customer"
              required
              value={field.value}
              error={customerError}
              onChange={(id) => {
                field.onChange(id);
                // Reset dependents when the customer changes (legacy behaviour).
                setValue('customer_contact_id', 0);
                setValue('contract_role', 'customer');
                onRolesChange([]);
              }}
            />
          )}
        />

        <Controller
          control={control}
          name="customer_contact_id"
          render={({ field }) => (
            <ContactSelect
              customerId={customerId}
              value={field.value}
              onChange={field.onChange}
              onRolesChange={onRolesChange}
            />
          )}
        />
      </FieldGrid>
    </SectionShell>
  );
}
